import { Button, Card, CircularProgress, Stack, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useGetFocusesDictionaryQuery } from '../../../../api'
import { DictionaryMultiSelect } from '../../../../components/ui-kit/DictionaryMultiSelect'
import { teamFormSchema, type TeamFormValues } from '../ZodValidationSchema'
import type { TDictionaryItem } from '../../../../api/types'
import { FormBlock } from '../../../../common/FormGenerator'
import { getFormConfig } from './settingsConfig'
import { CardStyle, TypStyle } from './style'

type DraftTeamFormProps = {
  onSubmitDraft: (data: TeamFormValues, focuses: TDictionaryItem[]) => void
  isLoading: boolean
}

const defaultValues: TeamFormValues = {
  title: '',
  city: '',
  status: '',
  descriptionShort: '',
  region: '',
  image: '',
  directionIds: [],
  goalsText: '',
  resourcesText: '',
  needsText: '',
}

export const DraftTeamForm = ({ onSubmitDraft, isLoading }: DraftTeamFormProps) => {
  const qFocuses = useGetFocusesDictionaryQuery()

  const methods = useForm({
    mode: 'onBlur',
    resolver: zodResolver(teamFormSchema),
    defaultValues,
  })

  const { handleSubmit } = methods

  if (!qFocuses.isError && !qFocuses.isSuccess) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (qFocuses.isError || !qFocuses.data) {
    return (
      <Typography color="error" textAlign="center" px={2}>
        Не удалось загрузить справочники. Обновите страницу.
      </Typography>
    )
  }

  const focuses = qFocuses.data

  return (
    <Stack alignItems="center" pt={{ xs: '15px', md: '50px' }} px={1.5}>
      <FormProvider {...methods}>
        <Stack gap={2} maxWidth={{ xs: "736px", md: "840px" }} width="100%">
          <Typography variant="h1" px={{ xs: 0, md: 2.5 }}>
            Создание команды
          </Typography>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Основные
            </Typography>
            <Stack gap={1.5}>
              <FormBlock formFieldConfig={getFormConfig('image')} />
              <FormBlock formFieldConfig={getFormConfig('title')} />
              <FormBlock formFieldConfig={getFormConfig('city')} />
              <FormBlock formFieldConfig={getFormConfig('status')} />
              <FormBlock formFieldConfig={getFormConfig('descriptionShort')} />
              <FormBlock formFieldConfig={getFormConfig('region')} />
            </Stack>
          </Card>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Направления
            </Typography>
            <DictionaryMultiSelect<TeamFormValues>
              name="directionIds"
              dictionary={focuses}
              placeholder="Выберите из списка"
            />
          </Card>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Цели
            </Typography>
            <FormBlock formFieldConfig={getFormConfig('goalsText')} />
          </Card>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Ресурсы
            </Typography>
            <FormBlock formFieldConfig={getFormConfig('resourcesText')} />
          </Card>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Потребности
            </Typography>
            <FormBlock formFieldConfig={getFormConfig('needsText')} />
          </Card>

          <Stack direction="row" spacing={1}>
            <Button
              variant="action"
              onClick={handleSubmit((data) => onSubmitDraft(data, focuses))}
              disabled={isLoading}
            >
              {isLoading ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} color="inherit" />
                  <span>Сохранение...</span>
                </Stack>
              ) : (
                'Сохранить'
              )}
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  )
}
